import { getOperationLogList, getOperationParse } from '@/api';
import { useQuery } from '@tanstack/react-query';
import { Card, Table, Tag, Pagination, Tooltip } from 'antd';
import { useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { useUserDetailModalStore } from '@/stores/userDetailModal';
import { relativeTimeFmt, absoluteTimeFmt } from '@/utils/timeFmt';

interface ParsedOperationLog {
  user_id?: number;
  user_name: string;
  uri: string;
  request: string;
  response: string;
  operation_time: string;
  optName: string;
  error: string;
  target: string;
  res: any;
  req: any;
}

export const OperationList: React.FC<{ user_id?: number }> = (props) => {
  const { openUserDetail } = useUserDetailModalStore();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });

  const opts = useQuery({
    queryKey: ['operationLogs', props.user_id, pagination.current, pagination.pageSize],
    queryFn: async () => {
      const offset = (pagination.current - 1) * pagination.pageSize;
      const res = await getOperationLogList({
        offset,
        limit: pagination.pageSize,
        order: 'desc',
        user_id: props.user_id,
      });
      return {
        data: res.data?.data?.map((log) => getOperationParse(log)) || [],
        total: res.data?.total || 0,
      };
    },
  });

  const columns: ColumnsType<ParsedOperationLog> = [
    {
      title: '用户',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 120,
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: '操作',
      dataIndex: 'optName',
      key: 'optName',
      width: 120,
    },
    {
      title: '目标',
      dataIndex: 'target',
      key: 'target',
      width: 120,
      render: (text: string) => (text ? <Tag color="green">{text}</Tag> : '-'),
    },
    {
      title: '状态',
      dataIndex: 'error',
      key: 'error',
      width: 120,
      render: (error: string, record: ParsedOperationLog) => (
        <Tooltip title={error || ''}>
          <Tag color={error ? 'red' : 'success'}>{error ? '失败' : '成功'}</Tag>
        </Tooltip>
      ),
    },
    {
      title: '操作时间',
      dataIndex: 'operation_time',
      key: 'operation_time',
      render: (text: string) => (
        <Tooltip title={absoluteTimeFmt(text)}>
          <span style={{ color: '#999', fontSize: 12 }}>{relativeTimeFmt(text)}</span>
        </Tooltip>
      ),
    },
  ];

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination({
      current: page,
      pageSize: pageSize,
    });
  };

  return (
    <Card title="操作日志" size="small">
      <Table<ParsedOperationLog>
        columns={columns}
        dataSource={opts.data?.data || []}
        loading={opts.isLoading}
        pagination={false}
        rowKey={(record, index) => `${record.user_id || 'unknown'}_${record.operation_time}_${index}`}
        scroll={{ x: 1000 }}
        size="small"
      />
      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Pagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={opts.data?.total || 0}
          onChange={handlePaginationChange}
          showSizeChanger
          showQuickJumper
          showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`}
          pageSizeOptions={['10', '20', '50', '100']}
        />
      </div>
    </Card>
  );
};
